import { Tooltip } from '@mantine/core'

import {
  calculateEmploymentSummary,
  toEmploymentDate,
  type EmploymentPeriod,
} from 'src/lib/employmentGapCalculator'
import { formatExperience } from 'src/lib/formatters'

const toEmploymentPeriod = (job: {
  startDate?: unknown
  endDate?: unknown
  isUnemployment?: boolean | null
}): EmploymentPeriod | null => {
  const startDate = toEmploymentDate(job.startDate)
  if (!startDate) return null

  return {
    startDate,
    endDate: toEmploymentDate(job.endDate),
    isUnemployment: job.isUnemployment ?? undefined,
  }
}

export const calculateSummary = (employmentExperiences) => {
  const periods = employmentExperiences.flatMap((job) => {
    const period = toEmploymentPeriod(job)
    return period ? [period] : []
  })
  const summary = calculateEmploymentSummary(periods)

  const convertMonthsToYears = (months) => months / 12

  return {
    yearsOfExperience: convertMonthsToYears(summary.totalMonthsExperience),
    numberOfJobs: summary.numberOfJobs,
    averageDuration: convertMonthsToYears(summary.averageDurationMonths),
    longestTenure: convertMonthsToYears(summary.longestTenureMonths),
    timeGap: convertMonthsToYears(summary.averageGapMonths),
  }
}

/**
 * Formats years as a compact string like "8.5 years", "3 months", etc.
 */
const fmtYears = (value: number): string => {
  if (value === 0) return '-'
  if (value < 1 / 12) return '-'
  if (value < 1) {
    const months = Math.round(value * 12)
    return `${months} mo`
  }
  const rounded = Math.round(value * 10) / 10
  // Drop the decimal if it's .0
  return rounded % 1 === 0 ? `${rounded} years` : `${rounded} years`
}

const StatItem = ({
  label,
  value,
  tooltip,
  hoverDetail,
}: {
  label: string
  value: string | number
  tooltip?: string
  hoverDetail?: string
}) => {
  const hoverText = [hoverDetail, tooltip].filter(Boolean).join(' — ')

  const content = (
    <div className="text-center">
      <div className="text-xs font-medium text-doubleNickel-gray-500">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-doubleNickel-gray-900">
        {value}
      </div>
    </div>
  )

  if (hoverText) {
    return (
      <Tooltip label={hoverText} position="top" withArrow>
        {content}
      </Tooltip>
    )
  }
  return content
}

const EmploymentSummaryCard = ({ application }) => {
  const allEmployment = application.employmentExperiences.filter(
    (job) => !job.isUnemployment
  )
  const dotEmployment = allEmployment.filter((job) => job.dotNumber)

  const totalSummary = calculateSummary(allEmployment)
  const dotSummary = calculateSummary(dotEmployment)

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border border-doubleNickel-gray-200 p-4">
      <div className="text-md font-semibold text-doubleNickel-black">
        Experience Summary
      </div>
      <div className="grid grid-cols-7 gap-2 px-4 py-3">
        <StatItem
          label="Total Experience"
          value={fmtYears(totalSummary.yearsOfExperience)}
          hoverDetail={formatExperience(totalSummary.yearsOfExperience)}
          tooltip="Total years across all employers"
        />
        <StatItem
          label="DOT Experience"
          value={fmtYears(dotSummary.yearsOfExperience)}
          hoverDetail={formatExperience(dotSummary.yearsOfExperience)}
          tooltip="Years with DOT-registered employers only"
        />
        <StatItem
          label="Total Jobs"
          value={totalSummary.numberOfJobs}
          hoverDetail={`${totalSummary.numberOfJobs} jobs`}
          tooltip="Total number of employers listed (excluding unemployment)"
        />
        <StatItem
          label="DOT Jobs"
          value={dotSummary.numberOfJobs}
          hoverDetail={`${dotSummary.numberOfJobs} of ${totalSummary.numberOfJobs} jobs`}
          tooltip="Number of jobs with DOT-registered employers"
        />
        <StatItem
          label="Avg Duration"
          value={fmtYears(dotSummary.averageDuration)}
          hoverDetail={formatExperience(dotSummary.averageDuration)}
          tooltip="Average time spent at each DOT employer"
        />
        <StatItem
          label="Longest Tenure"
          value={fmtYears(dotSummary.longestTenure)}
          hoverDetail={formatExperience(dotSummary.longestTenure)}
          tooltip="Longest single stretch at one DOT employer"
        />
        <StatItem
          label="Avg Gap"
          value={fmtYears(dotSummary.timeGap)}
          hoverDetail={formatExperience(dotSummary.timeGap)}
          tooltip="Average time between DOT jobs"
        />
      </div>
    </div>
  )
}

export default EmploymentSummaryCard
